import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/index';
import { Table } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { apiGetPage } from '../../request/api';
import { useCustomData } from '../../pages/context/serviceCustomDataProvider';

interface ServiceFeature {
    id: string;
    service: string;
    basic: string | boolean;
    advanced: string | boolean;
    isActionRow?: boolean;
}

interface ServiceTableProps {
    handleViewChange: (newView: string, data?: any) => void;
    data?: any;
    onOpenModal?: (plan: any) => void;   // ✅ plan 是完整套餐对象
    onOpenNote?: (type: 'basic' | 'premium') => void;
}

interface ButtonGroupProps {
    themeColor: string;
    handleViewChange: (newView: string, data?: any) => void;
    record: ServiceFeature;
    packageData: any;   // ✅ 套餐对象
    onOpenModal?: (plan: any) => void;
    onOpenNote?: (type: 'basic' | 'premium') => void;
}

interface PackageInfo {
    id: string;
    commodityName: string;
}

interface ServiceItem {
    belongToCommodity: string;
    serviceScope: string;
    littleRemark: string | null;
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({ themeColor, handleViewChange, record, packageData, onOpenModal, onOpenNote }) => {
    const getContrastColor = (hexColor: string) => {
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 128 ? '#000' : '#fff';
    };
    const { getValue } = useCustomData();
    const iwant = getValue('iwant');
    const knowMore = getValue('knowMore');

    return (
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button
                style={{
                    height: '36px',
                    padding: "0",
                    lineHeight: "36px",
                    width: "160px",
                    backgroundColor: themeColor,
                    color: getContrastColor(themeColor),
                    border: 'none',
                    borderRadius: '18px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    flex: 1,
                    maxWidth: '160px'
                }}
                onClick={() => {
                    onOpenModal?.(packageData)
                }}   
            >
                {iwant}
            </button>
            <button
                style={{
                    color: themeColor,
                    width: '160px',
                    height: '36px',
                    padding: "0",
                    lineHeight: "36px",
                    background: '#ffffff',
                    border: `1px solid ${themeColor}`,
                    borderRadius: '18px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    flex: 1,
                    maxWidth: '160px'
                }}
                onClick={() => handleViewChange('service-detail', packageData?.id)}   // ✅ 用套餐 id
            >
                {knowMore}
            </button>
        </div>
    );
};

const ServiceTable: React.FC<ServiceTableProps> = ({ handleViewChange, data, onOpenModal, onOpenNote }) => {
    const languageCode = localStorage.getItem('siteLanguage') || 'zh';
    const themeColor = useSelector((state: RootState) => state.themeColor.value);

    const [tableData, setTableData] = useState<ServiceFeature[]>([]);
    const [basicName, setBasicName] = useState('基础版');
    const [premiumName, setPremiumName] = useState('高级版');
    const [packageList, setPackageList] = useState<any[]>([]);   // ✅ 存储套餐对象
    const { getValue } = useCustomData();
    const serviceRange = getValue('serviceRange');

    const getContrastColor = (hexColor: string) => {
        const r = parseInt(hexColor.slice(1, 3), 16);
        const g = parseInt(hexColor.slice(3, 5), 16);
        const b = parseInt(hexColor.slice(5, 7), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 128 ? '#000' : '#fff';
    };

    const headerStyle = {
        backgroundColor: themeColor,
        color: getContrastColor(themeColor),
        fontWeight: 400,
        fontSize: '24px',
        letterSpacing: '0.9px',
        height: '97px',
        textAlign: 'center' as const,
    };

    const renderCell = (value: string | boolean, record: ServiceFeature, isFirstColumn: boolean = false, columnKey?: 'basic' | 'advanced') => {
        if (record.isActionRow) {
            if (isFirstColumn) return <div></div>;

            const packageId = columnKey === 'basic'
                ? '89762F6A770A46FE83B7A39C5230E371'
                : 'D7074AA0788643E68CA1B6F03F996EB3';

            const pkgObj = packageList.find(item => item.id === packageId);  // ✅ 找套餐对象

            return (
                <ButtonGroup
                    themeColor={themeColor}
                    handleViewChange={handleViewChange}
                    record={record}
                    packageData={pkgObj}   // ✅ 传完整对象
                    onOpenNote={onOpenNote}
                    onOpenModal={onOpenModal}
                />
            );
        }
        if (typeof value === 'string') {
            return (
                <span style={{ color: isFirstColumn ? '#33364b' : themeColor, fontSize: '18px' }}>
                    {value}
                </span>
            );
        }
        return value ? (
            <CheckOutlined style={{ color: themeColor, fontSize: '18px' }} />
        ) : (
            <CloseOutlined style={{ color: '#33364b', fontSize: '18px' }} />
        );
    };

    const columns = [
        {
            title: serviceRange,
            dataIndex: 'service',
            key: 'service',
            width: 400,
            render: (text: string, record: ServiceFeature) => renderCell(text, record, true),
            onHeaderCell: () => ({ style: headerStyle })
        },
        {
            title: basicName,
            dataIndex: 'basic',
            key: 'basic',
            width: 400,
            render: (value: string | boolean, record: ServiceFeature) => renderCell(value, record, false, 'basic'),
            onHeaderCell: () => ({ style: headerStyle })
        },
        {
            title: premiumName,
            dataIndex: 'advanced',
            key: 'advanced',
            width: 400,
            render: (value: string | boolean, record: ServiceFeature) => renderCell(value, record, false, 'advanced'),
            onHeaderCell: () => ({ style: headerStyle })
        }
    ];

    useEffect(() => {
        // 1. 获取套餐信息
        apiGetPage({
            tid: '72D99CCB9A7E439184BF9EF7CABC49BA',
            page: 1,
            languagecode: languageCode,
            size: 100,
            search: 'isPackage:1'
        }).then(res => {
            const packageData = res.data.data.rows || [];
            setPackageList(packageData);   // ✅ 存套餐数据

            const packageMap: Record<string, string> = {};
            packageData.forEach((pkg: PackageInfo) => {
                packageMap[pkg.id] = pkg.commodityName;
            });

            if (packageMap['89762F6A770A46FE83B7A39C5230E371']) {
                setBasicName(packageMap['89762F6A770A46FE83B7A39C5230E371']);
            }
            if (packageMap['D7074AA0788643E68CA1B6F03F996EB3']) {
                setPremiumName(packageMap['D7074AA0788643E68CA1B6F03F996EB3']);
            }

            // 2. 获取服务项信息
            apiGetPage({
                tid: '0B5BEE67FE7B419C8C33CFD33A11CA5F',
                page: 1,
                size: 100,
                languagecode: languageCode,
                search: `belongToCommodity:D7074AA0788643E68CA1B6F03F996EB3/belongToCommodity:89762F6A770A46FE83B7A39C5230E371`,
            }).then(serviceRes => {
                const serviceRows = serviceRes.data.data.rows || [];

                const serviceGroups: Record<string, {
                    basic: ServiceItem[],
                    advanced: ServiceItem[]
                }> = {};

                serviceRows.forEach((item: ServiceItem) => {
                    if (!serviceGroups[item.serviceScope]) {
                        serviceGroups[item.serviceScope] = {
                            basic: [],
                            advanced: []
                        };
                    }

                    if (item.belongToCommodity === '89762F6A770A46FE83B7A39C5230E371') {
                        serviceGroups[item.serviceScope].basic.push(item);
                    } else if (item.belongToCommodity === 'D7074AA0788643E68CA1B6F03F996EB3') {
                        serviceGroups[item.serviceScope].advanced.push(item);
                    }
                });

                const tableRows: ServiceFeature[] = [];
                const remarkRows: ServiceFeature[] = [];

                Object.entries(serviceGroups).forEach(([serviceScope, group]) => {
                    const hasBasic = group.basic.length > 0;
                    const hasAdvanced = group.advanced.length > 0;

                    const basicRemark = group.basic.find(item =>
                        item.littleRemark &&
                        ((languageCode === 'zh' && item.littleRemark.length <= 7) ||
                            (languageCode === 'en' && item.littleRemark.length <= 25))
                    )?.littleRemark;

                    const advancedRemark = group.advanced.find(item =>
                        item.littleRemark &&
                        ((languageCode === 'zh' && item.littleRemark.length <= 7) ||
                            (languageCode === 'en' && item.littleRemark.length <= 25))
                    )?.littleRemark;

                    const rowData: ServiceFeature = {
                        id: serviceScope,
                        service: serviceScope,
                        basic: basicRemark || hasBasic,
                        advanced: advancedRemark || hasAdvanced
                    };

                    if (basicRemark || advancedRemark) {
                        remarkRows.push(rowData);
                    } else {
                        tableRows.push(rowData);
                    }
                });

                const getRowRank = (row: ServiceFeature) => {
                    const isBasicString = typeof row.basic === 'string';
                    const isAdvancedString = typeof row.advanced === 'string';
                    const isBasicTrue = row.basic === true;
                    const isAdvancedTrue = row.advanced === true;
                    const isBasicFalse = row.basic === false;
                    const isAdvancedFalse = row.advanced === false;

                    if (isBasicString && isAdvancedString) return 1;
                    if (isBasicTrue && isAdvancedTrue) return 2;
                    if ((isBasicString && isAdvancedTrue) || (isAdvancedString && isBasicTrue)) return 3;
                    if (isBasicFalse || isAdvancedFalse) return 4;
                    return 99;
                };

                let combinedRows = [...remarkRows, ...tableRows].sort((a, b) =>
                    getRowRank(a) - getRowRank(b)
                );

                combinedRows.push({
                    id: "action-row",
                    service: "",
                    basic: true,
                    advanced: true,
                    isActionRow: true
                });

                setTableData(combinedRows);
            });
        });
    }, [languageCode]);

    return (
        <div className='service-table' style={{ borderRadius: '40px', overflow: 'hidden' }}>
            <Table
                columns={columns}
                dataSource={tableData}
                rowKey="id"
                pagination={false}
                bordered={true}
                style={{ margin: 16 }}
                scroll={{ x: 1200 }}
                components={{
                    header: {
                        cell: (props: any) => (
                            <th
                                {...props}
                                style={{
                                    ...props.style,
                                    ...headerStyle,
                                    borderColor: themeColor,
                                    transition: 'background-color 0.3s'
                                }}
                            />
                        )
                    },
                    body: {
                        cell: (props: any) => (
                            <td
                                {...props}
                                style={{
                                    ...props.style,
                                    height: '65px',
                                    textAlign: 'center',
                                    backgroundColor: 'rgba(14,8,249,0.05)',
                                    borderColor: 'rgba(14,8,249,0.1)',
                                    fontSize: '18px',
                                }}
                            />
                        ),
                        row: (props: any, index: number) => {
                            const isLastRow = index === tableData.length - 1;
                            return (
                                <tr
                                    {...props}
                                    style={{
                                        ...props.style,
                                        borderBottom: isLastRow ? `1px solid rgba(14,8,249,0.1)` : 'none',
                                    }}
                                />
                            );
                        }
                    }
                }}
            />
        </div>
    );
};

export default ServiceTable;
